import { useState, useEffect } from 'react';
import {
  Box, Paper, Stack, Typography, Chip, Button, IconButton, TextField, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel,
  Snackbar, Alert, CircularProgress, Tooltip, LinearProgress, alpha, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

import { useApi } from '../../../hooks/useApi';

/**
 * Knowledge tab — full CRUD on accountprofile entries for one account. Lifted
 * verbatim from the original Customer/Detail.js so behaviour is unchanged; it
 * just lives inside a tab now and owns its own toolbar (count / filter / add /
 * refresh) instead of the page header.
 */
export default function KnowledgeTab({ code }) {
  const jApi = useApi();
  const theme = useTheme();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [filter, setFilter] = useState('');

  const [editing, setEditing] = useState(null);   // null | { id?, topic, tags, content, scope, audit_function_paths }
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Plain function — see the AccountProfileEditor comment about useApi+useCallback.
  const load = async () => {
    if (!code) return;
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const pResp = await jApi.request('GET', `cma/accountprofile?account=${encodeURIComponent(code)}`);
      const list = Array.isArray(pResp.data) ? pResp.data : [];
      list.sort((a, b) => (a.topic || '').localeCompare(b.topic || ''));
      setRows(list);
    } catch (ex) {
      setErrorMessage(ex?.toString?.() || 'Failed to load knowledge entries');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const openNew = () => setEditing({
    topic:                '',
    tags:                 '',
    content:              '',
    scope:                'matched',
    audit_function_paths: [],
  });

  const openEdit = (row) => setEditing({
    id:                   row.id,
    topic:                row.topic || '',
    tags:                 Array.isArray(row.tags) ? row.tags.join(', ') : '',
    content:              row.content || '',
    scope:                row.scope || 'matched',
    audit_function_paths: row.audit_function_paths || [],
  });

  const closeEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const tagsArr = editing.tags
        ? editing.tags.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      const body = {
        account:              code,
        topic:                editing.topic,
        content:              editing.content,
        scope:                editing.scope,
        tags:                 tagsArr,
        audit_function_paths: editing.audit_function_paths || [],
      };
      if (editing.id) body.id = editing.id;

      const method = editing.id ? 'PATCH' : 'POST';
      await jApi.request(method, 'cma/accountprofile', body);

      setToast({ severity: 'success', text: editing.id ? 'Profile updated' : 'Profile added' });
      closeEdit();
      load();
    } catch (ex) {
      const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Save failed';
      setToast({ severity: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete profile entry "${row.topic}"?`)) return;
    try {
      await jApi.request('DELETE', `cma/accountprofile/${row.id}?account=${encodeURIComponent(row.account)}`);
      setToast({ severity: 'success', text: 'Profile deleted' });
      load();
    } catch (ex) {
      setToast({ severity: 'error', text: ex?.toString?.() || 'Delete failed' });
    }
  };

  const filtered = filter.trim()
    ? rows.filter(r => {
        const n = filter.trim().toLowerCase();
        return (r.topic || '').toLowerCase().includes(n)
            || (r.content || '').toLowerCase().includes(n)
            || (Array.isArray(r.tags) && r.tags.some(t => t.toLowerCase().includes(n)));
      })
    : rows;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {isLoading ? 'Loading…' : `${rows.length} knowledge entr${rows.length === 1 ? 'y' : 'ies'}`}
          </Typography>
          <TextField
            size="small"
            placeholder="Filter…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment> }}
            sx={{ minWidth: 220 }}
          />
          <Button variant="contained" size="small" startIcon={<AddIcon/>} onClick={openNew}>Add entry</Button>
          <IconButton size="small" onClick={load} disabled={isLoading}><RefreshIcon/></IconButton>
        </Stack>
        {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
      </Paper>

      {errorMessage && (
        <Paper sx={{ p: 2, mb: 2, background: alpha(theme.palette.error.main, 0.1),
          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
          <Typography variant="body2" color="error.main">{errorMessage}</Typography>
        </Paper>
      )}

      {!isLoading && rows.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>No knowledge entries yet.</Typography>
          <Button variant="outlined" startIcon={<AddIcon/>} onClick={openNew}>Add the first entry</Button>
        </Paper>
      )}

      {!isLoading && rows.length > 0 && filtered.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">No entries match that filter.</Typography>
        </Paper>
      )}

      {!isLoading && filtered.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 200 }}>Topic</TableCell>
                <TableCell>Content</TableCell>
                <TableCell sx={{ width: 180 }}>Tags / Scope</TableCell>
                <TableCell sx={{ width: 180 }}>Last updated</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{row.topic}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>{row.content}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Stack spacing={0.5}>
                      {row.directive?.kind && (
                        <Tooltip title="Drives Technical Health behavior (a per-customer one-off)">
                          <Chip size="small" label={row.directive.kind} color="secondary" variant="outlined" sx={{ alignSelf: 'flex-start', height: 18 }}/>
                        </Tooltip>
                      )}
                      {row.scope === 'always' && (
                        <Chip size="small" label="always" color="info" variant="outlined" sx={{ alignSelf: 'flex-start', height: 18 }}/>
                      )}
                      {Array.isArray(row.tags) && row.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {row.tags.map(t => (
                            <Chip key={t} size="small" label={t} variant="outlined" sx={{ height: 18 }}/>
                          ))}
                        </Stack>
                      )}
                      {Array.isArray(row.audit_function_paths) && row.audit_function_paths.length > 0 && (
                        <Tooltip title={row.audit_function_paths.join('\n')}>
                          <Chip size="small" label={`${row.audit_function_paths.length} audit${row.audit_function_paths.length === 1 ? '' : 's'}`}
                            variant="outlined" sx={{ alignSelf: 'flex-start', height: 18 }}/>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2">{row.lastUpdated?.slice(0, 10) || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.last_updated_by || ''}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                    <IconButton size="small" onClick={() => openEdit(row)} title="Edit"><EditIcon fontSize="small"/></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(row)} title="Delete"><DeleteIcon fontSize="small"/></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={!!editing} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? 'Edit profile entry' : 'Add profile entry'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Topic" required autoFocus
              value={editing?.topic || ''}
              onChange={e => setEditing({ ...editing, topic: e.target.value })}
              placeholder="WiFi Coverage"
              helperText="Short label — usually matches the audit Title"
            />
            <TextField
              label="Tags"
              value={editing?.tags || ''}
              onChange={e => setEditing({ ...editing, tags: e.target.value })}
              placeholder="wifi, heatmap"
              helperText="Comma-separated"
            />
            <TextField
              label="Content" required multiline rows={6}
              value={editing?.content || ''}
              onChange={e => setEditing({ ...editing, content: e.target.value })}
              placeholder="Heatmap completed 2026-03-15 by Ryan F.…"
            />
            <FormControl size="small">
              <InputLabel>Scope</InputLabel>
              <Select
                label="Scope"
                value={editing?.scope || 'matched'}
                onChange={e => setEditing({ ...editing, scope: e.target.value })}
              >
                <MenuItem value="matched">Matched — only injected when an audit's path matches</MenuItem>
                <MenuItem value="always">Always — every AI audit for this customer</MenuItem>
              </Select>
            </FormControl>
            {editing?.scope === 'matched' && (
              <TextField
                label="Audit function paths"
                value={(editing?.audit_function_paths || []).join('\n')}
                onChange={e => setEditing({
                  ...editing,
                  audit_function_paths: e.target.value.split('\n').map(s => s.trim()).filter(Boolean),
                })}
                multiline rows={3}
                placeholder={"cma/audit/network/Test-NetworkWifiCoverage.ps1\ncma/audit/network/Test-NetworkCapacity.ps1"}
                helperText="One Test-* function path per line. Empty = entry won't auto-inject; use 'always' if you want it always included."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16}/> : <SaveIcon/>}
            onClick={handleSave}
            disabled={saving || !editing?.topic || !editing?.content}
          >
            {saving ? 'Saving…' : (editing?.id ? 'Save' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
